import React, { useState, useRef, useEffect } from "react";
import { popularInteriorDesign } from "./useHook";
import Swal from "sweetalert2";

const AddPopularInteriorDesign = ({ isOpen, onClose }) => {
  const { addPopularInteriorDesign, getAllInteriorDesignNamesForPopularDesign, getAllInteriorDesigns } = popularInteriorDesign();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [interiorDesignNames, setInteriorDesignNames] = useState([]);
  const [selectedDesigns, setSelectedDesigns] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentPopularDesigns, setCurrentPopularDesigns] = useState([]);
  const [interiorDesignData, setInteriorDesignData] = useState([]);
  const modalRef = useRef(null);
  const dropdownRef = useRef(null);

  const MAX_SELECTIONS = 4;
  useEffect(() => {
    if (isOpen) {
      getAllInteriorDesignNamesForPopularDesign(setInteriorDesignNames);
      getAllInteriorDesigns(setInteriorDesignData);
    }
  }, [isOpen]);

  useEffect(() => {
    if (interiorDesignData.length > 0) {
      const popular = interiorDesignData.filter(
        (design) => design.onHomeScreen === true
      );
      setCurrentPopularDesigns(popular);
      setSelectedDesigns(popular.map((design) => design.id));
    }
  }, [interiorDesignData]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isDropdownOpen &&
        modalRef.current &&
        !modalRef.current.contains(event.target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isDropdownOpen]);

  const handleCheckboxChange = (id) => {
    setSelectedDesigns((prev) => {
      if (prev.includes(id)) {
        return prev.filter((item) => item !== id);
      }
      if (prev.length >= MAX_SELECTIONS) {
        return prev;
      }
      return [...prev, id];
    });
  };

  const removeSelectedDesign = (id) => {
    setSelectedDesigns((prev) => prev.filter((item) => item !== id));
  };

  const handleSave = async () => {
    if (selectedDesigns.length === 0) {
      Swal.fire({
        icon: "warning",
        title: "No Selection",
        text: "Please select at least one interior design.",
      });
      return;
    }
    setIsSubmitting(true);
    try {
      const designData = {
        interiorDesignIds: selectedDesigns,
      };
      const result = await addPopularInteriorDesign(designData);
      if (result) {
        Swal.fire({
          title: "Success",
          text: "Popular interior designs saved successfully.",
          icon: "success",
          confirmButtonText: "OK",
        }).then(() => {
          onClose();
        });
      }
    } catch (error) {
      console.error("Error saving popular interior designs:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to save popular interior designs. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-800 bg-opacity-50">
      <div ref={modalRef} className="bg-white p-4 rounded shadow-lg w-96">
        <h2 className="text-xl font-bold mb-4">
          Add/Delete Popular Interior Designs
        </h2>
        <div className="mb-4">
          <h3 className="text-md font-bold">Popular Interior Designs</h3>
          <p className="text-sm text-gray-500 mb-2">
            Maximum {MAX_SELECTIONS} designs can be selected
            {selectedDesigns.length > 0 &&
              ` (${selectedDesigns.length}/${MAX_SELECTIONS} selected)`}
          </p>
          <ul className="mt-2 flex flex-wrap gap-2">
            {selectedDesigns.length > 0 ? (
              selectedDesigns.map((id) => {
                const design = interiorDesignData.find((d) => d.id === id);
                return (
                  <li
                    key={id}
                    className="bg-blue-100 text-blue-800 px-2 py-1 rounded-md text-sm flex items-center gap-1"
                  >
                    <span>{design?.title || design?.name || "Unknown Design"}</span>
                    <button
                      type="button"
                      onClick={() => removeSelectedDesign(id)}
                      className="text-blue-600 hover:text-blue-800 font-bold"
                    >
                      ×
                    </button>
                  </li>
                );
              })
            ) : (
              <li className="text-gray-500 text-sm">No designs selected</li>
            )}
          </ul>
        </div>
        <div className="relative" ref={dropdownRef}>
          <button
            className="w-full border border-gray-300 bg-white px-4 py-2 rounded-md text-left focus:outline-none"
            onClick={() => setIsDropdownOpen((prev) => !prev)}
          >
            Add more designs...
          </button>
          {isDropdownOpen && (
            <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow max-h-48 overflow-y-auto">
              {Array.isArray(interiorDesignNames) &&
                interiorDesignNames.map((design) => (
                  <label
                    key={design.id}
                    className={`cursor-pointer px-4 py-2 hover:bg-gray-100 flex items-center ${selectedDesigns.length >= MAX_SELECTIONS &&
                      !selectedDesigns.includes(design.id)
                      ? "opacity-50"
                      : ""
                      }`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedDesigns.includes(design.id)}
                      onChange={() => handleCheckboxChange(design.id)}
                      disabled={
                        selectedDesigns.length >= MAX_SELECTIONS &&
                        !selectedDesigns.includes(design.id)
                      }
                      className="mr-2"
                    />
                    <span>{design.title}</span>
                  </label>
                ))}
            </div>
          )}
        </div>
        <div className="mt-4 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="btn btn-reset-filter justify-center"
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={isSubmitting}
            className={`btn btn-primary justify-center ${
              isSubmitting ? "opacity-50 cursor-not-allowed" : ""
            }`}
          >
            {isSubmitting ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddPopularInteriorDesign;
